/**
 * Hand-off configuration for the "continue editing" integrations on the download pages.
 *
 * Both hosts are environment driven (`NEXT_PUBLIC_CLIPSNAP_URL` / `NEXT_PUBLIC_PROPDF_URL`)
 * so the hand-off can be pointed at the staging deployments before it is enabled in
 * production. When a host is not configured the corresponding action is hidden.
 */

export type ExternalEditor = "clipsnap" | "propdf";

/**
 * Path on the ClipSnap host that accepts an already-hosted file. The trailing slash is required —
 * ClipSnap runs with `trailingSlash: true` and would otherwise 308 on every hand-off.
 */
const CLIPSNAP_EDITOR_PATH = "/editor/";

/** Query param ClipSnap reads the freeconvert.com file URL from. */
const CLIPSNAP_EXTERNAL_URL_PARAM = "external_url";

/**
 * ClipSnap runs background removal automatically on some entry points. The editor hand-off must
 * open the image untouched, so we opt out explicitly — the flag mirrors ClipSnap's own
 * `skipAutoBgRemoval` switch rather than inverting it.
 */
const CLIPSNAP_EXTRA_PARAMS: Record<string, string> = {
    disable_remove_background: "true",
};

/**
 * ProPDF hand-off contract (agreed with the ProPDF team): every tool/conversion page accepts a
 * repeated `urls` query parameter holding publicly reachable file URLs, and feeds them through its
 * normal URL-upload ingestion. Our output is always an image, so we land on the generic
 * image-to-PDF page rather than a PDF-only editor. The value is percent-encoded by `URLSearchParams`
 * (`&`, `#`, `+` included), as the contract requires.
 */
const PROPDF_EDITOR_PATH = "/image-to-pdf";
const PROPDF_EXTERNAL_URL_PARAM = "urls";
const PROPDF_EXTRA_PARAMS: Record<string, string> = {};

const CLIPSNAP_SUPPORTED_EXTENSIONS = ["jpg", "jpeg", "png", "webp"];

export const isClipSnapSupportedFile = (fileName: string): boolean =>
    CLIPSNAP_SUPPORTED_EXTENSIONS.includes(fileName.split(".").pop()?.toLowerCase() ?? "");

/**
 * `process.env.NEXT_PUBLIC_*` has to be referenced literally so Next.js can inline it at
 * build time — it cannot be read through a computed key.
 */
export const getExternalEditorHost = (editor: ExternalEditor): string =>
    (editor === "clipsnap" ? process.env.NEXT_PUBLIC_CLIPSNAP_URL : process.env.NEXT_PUBLIC_PROPDF_URL) || "";

export const isExternalEditorConfigured = (editor: ExternalEditor): boolean => !!getExternalEditorHost(editor);

// Encoded so XSS-looking file names don't trip Cloudflare's WAF on clipsnap.com.
const toBase64Url = (fileUrl: string): string =>
    btoa(new URL(fileUrl).href).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

/** ClipSnap tool pages (production URL structure). */
export const CLIPSNAP_TOOL_PATHS = {
    "background-remover": "/tools/background-remover/",
    "transparent-background": "/tools/background-remover/transparent-background/",
    "white-background": "/tools/background-color-changer/white-background/",
    "image-upscaler": "/tools/image-upscaler/",
    "image-enhancer": "/tools/image-enhancer/",
    "magic-eraser": "/tools/cleanup-pictures/magic-eraser/",
    "generative-fill": "/tools/generative-fill/",
    "blur-background": "/tools/blur-background/",
    "ai-background-generator": "/tools/ai-background-generator/",
    "ai-image-generator": "/tools/ai-image-generator/",
    "ai-art-generator": "/tools/ai-image-generator/ai-art-generator/",
} as const;

export type ClipSnapTool = keyof typeof CLIPSNAP_TOOL_PATHS;

/** Plain link to a ClipSnap tool page. */
export const getClipSnapToolLink = (tool: ClipSnapTool): string =>
    `${getClipSnapSiteOrigin()}${CLIPSNAP_TOOL_PATHS[tool]}`;

/** The AI tools promoted in the "Continue with AI" section, in display order. */
export const CLIPSNAP_AI_TOOLS = [
    { slug: "background-remover", labelKey: "ai-tool-background-remover", tipKey: "ai-tool-background-remover-tip" },
    { slug: "white-background", labelKey: "ai-tool-white-background", tipKey: "ai-tool-white-background-tip" },
    {
        slug: "transparent-background",
        labelKey: "ai-tool-transparent-background",
        tipKey: "ai-tool-transparent-background-tip",
    },
    { slug: "image-upscaler", labelKey: "ai-tool-image-upscaler", tipKey: "ai-tool-image-upscaler-tip" },
    { slug: "magic-eraser", labelKey: "ai-tool-magic-eraser", tipKey: "ai-tool-magic-eraser-tip" },
    { slug: "image-enhancer", labelKey: "ai-tool-image-enhancer", tipKey: "ai-tool-image-enhancer-tip" },
    { slug: "generative-fill", labelKey: "ai-tool-generative-fill", tipKey: "ai-tool-generative-fill-tip" },
    { slug: "blur-background", labelKey: "ai-tool-blur-background", tipKey: "ai-tool-blur-background-tip" },
    {
        slug: "ai-background-generator",
        labelKey: "ai-tool-background-generator",
        tipKey: "ai-tool-background-generator-tip",
    },
] as const;

export type ClipSnapAiTool = (typeof CLIPSNAP_AI_TOOLS)[number];

/** The ClipSnap origin (`NEXT_PUBLIC_CLIPSNAP_URL`) without a trailing slash, for building links. */
export const getClipSnapSiteOrigin = (): string => getExternalEditorHost("clipsnap").replace(/\/$/, "");

const buildClipSnapUrl = (path: string, params: Record<string, string> = {}): string | null => {
    const host = getExternalEditorHost("clipsnap");
    if (!host) return null;
    try {
        const url = new URL(path, host);
        Object.entries(params).forEach(([key, value]) => url.searchParams.set(key, value));
        return url.toString();
    } catch {
        return null;
    }
};

/** A ClipSnap tool's landing page. The user picks their own image there. */
export const getClipSnapToolUrl = (tool: ClipSnapTool): string | null => buildClipSnapUrl(CLIPSNAP_TOOL_PATHS[tool]);

/**
 * The ClipSnap editor, used by the "Continue editing with ClipSnap" CTA. With a file we run the
 * full hand-off (including the background-removal opt-out); without one the editor opens empty.
 */
export const getClipSnapEditorUrl = (fileUrl?: string): string | null =>
    fileUrl ? buildExternalEditorUrl("clipsnap", fileUrl) : buildClipSnapUrl(CLIPSNAP_EDITOR_PATH);

/**
 * Builds the deep link that opens `fileUrl` in the target editor.
 * Returns `null` when the host is not configured or either URL is unusable, so the caller can
 * surface an error instead of navigating somewhere broken.
 */
export const buildExternalEditorUrl = (editor: ExternalEditor, fileUrl: string): string | null => {
    const host = getExternalEditorHost(editor);
    if (!host || !fileUrl) return null;

    const path = editor === "clipsnap" ? CLIPSNAP_EDITOR_PATH : PROPDF_EDITOR_PATH;
    const externalUrlParam = editor === "clipsnap" ? CLIPSNAP_EXTERNAL_URL_PARAM : PROPDF_EXTERNAL_URL_PARAM;
    const extraParams = editor === "clipsnap" ? CLIPSNAP_EXTRA_PARAMS : PROPDF_EXTRA_PARAMS;

    try {
        const url = new URL(path, host);
        url.searchParams.set(externalUrlParam, editor === "clipsnap" ? toBase64Url(fileUrl) : fileUrl);
        Object.entries(extraParams).forEach(([key, value]) => url.searchParams.set(key, value));
        return url.toString();
    } catch {
        return null;
    }
};
